import {
  SalaryCountryPack,
  SalarySubdivision,
  TaxBracket,
  GoldenTaxTestCase,
  SalaryCommandCenterTelemetry,
  TaxRuleDiff,
  TaxDataIncident,
  DeliverableP6,
} from '../types';

export const SALARY_COUNTRY_PACKS: Record<string, SalaryCountryPack> = {
  // 1. UNITED STATES (US-2026)
  US: {
    countryCode: 'US',
    countryName: 'United States',
    defaultCurrency: 'USD',
    taxPeriodId: 'US-2026',
    taxPeriodType: 'CALENDAR_YEAR',
    taxYearLabel: 'Tax Year 2026 (IRS Indexed)',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'US-FED-2026-v3.1',
    verifiedAt: '2026-08-20',
    reviewedBy: 'Senior US Payroll Tax Council (YamooPay)',
    officialSources: [
      {
        name: 'Internal Revenue Service (IRS)',
        authority: 'Federal Tax Administration',
        url: 'https://www.irs.gov/newsroom/irs-releases-tax-inflation-adjustments-for-tax-year-2026',
        lastVerified: '2026-08-20',
      },
      {
        name: 'Social Security Administration (SSA)',
        authority: 'OASDI & Medicare Board of Trustees',
        url: 'https://www.ssa.gov/oact/cola/cbb.html',
        lastVerified: '2026-08-20',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'MARRIED_JOINT', 'MARRIED_SEPARATE', 'HEAD_OF_HOUSEHOLD'],
    defaultPayFrequency: 'biweekly',
    hoursPerWeekDefault: 40,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 15000,
      MARRIED_JOINT: 30000,
      MARRIED_SEPARATE: 15000,
      HEAD_OF_HOUSEHOLD: 22500,
      WIDOWED: 30000,
      STANDARD: 15000,
    },
    federalBrackets: {
      SINGLE: [
        { min: 0, max: 11925, rate: 0.10, label: '10% Tier', marginalDescription: '$0 to $11,925 taxed at 10%' },
        { min: 11925, max: 48475, rate: 0.12, label: '12% Tier', marginalDescription: '$11,925 to $48,475 taxed at 12%' },
        { min: 48475, max: 103350, rate: 0.22, label: '22% Tier', marginalDescription: '$48,475 to $103,350 taxed at 22%' },
        { min: 103350, max: 197300, rate: 0.24, label: '24% Tier', marginalDescription: '$103,350 to $197,300 taxed at 24%' },
        { min: 197300, max: 250525, rate: 0.32, label: '32% Tier', marginalDescription: '$197,300 to $250,525 taxed at 32%' },
        { min: 250525, max: 626350, rate: 0.35, label: '35% Tier', marginalDescription: '$250,525 to $626,350 taxed at 35%' },
        { min: 626350, max: null, rate: 0.37, label: '37% Tier', marginalDescription: 'Income over $626,350 taxed at 37%' },
      ],
      MARRIED_JOINT: [
        { min: 0, max: 23850, rate: 0.10, label: '10% Tier', marginalDescription: '$0 to $23,850 taxed at 10%' },
        { min: 23850, max: 96950, rate: 0.12, label: '12% Tier', marginalDescription: '$23,850 to $96,950 taxed at 12%' },
        { min: 96950, max: 206700, rate: 0.22, label: '22% Tier', marginalDescription: '$96,950 to $206,700 taxed at 22%' },
        { min: 206700, max: 394600, rate: 0.24, label: '24% Tier', marginalDescription: '$206,700 to $394,600 taxed at 24%' },
        { min: 394600, max: 501050, rate: 0.32, label: '32% Tier', marginalDescription: '$394,600 to $501,050 taxed at 32%' },
        { min: 501050, max: 751600, rate: 0.35, label: '35% Tier', marginalDescription: '$501,050 to $751,600 taxed at 35%' },
        { min: 751600, max: null, rate: 0.37, label: '37% Tier', marginalDescription: 'Income over $751,600 taxed at 37%' },
      ],
      HEAD_OF_HOUSEHOLD: [
        { min: 0, max: 17000, rate: 0.10, label: '10% Tier', marginalDescription: '$0 to $17,000 taxed at 10%' },
        { min: 17000, max: 64850, rate: 0.12, label: '12% Tier', marginalDescription: '$17,000 to $64,850 taxed at 12%' },
        { min: 64850, max: 103350, rate: 0.22, label: '22% Tier', marginalDescription: '$64,850 to $103,350 taxed at 22%' },
        { min: 103350, max: 197300, rate: 0.24, label: '24% Tier', marginalDescription: '$103,350 to $197,300 taxed at 24%' },
        { min: 197300, max: 250500, rate: 0.32, label: '32% Tier', marginalDescription: '$197,300 to $250,500 taxed at 32%' },
        { min: 250500, max: 626350, rate: 0.35, label: '35% Tier', marginalDescription: '$250,500 to $626,350 taxed at 35%' },
        { min: 626350, max: null, rate: 0.37, label: '37% Tier', marginalDescription: 'Income over $626,350 taxed at 37%' },
      ],
      MARRIED_SEPARATE: [
        { min: 0, max: 11925, rate: 0.10, label: '10% Tier', marginalDescription: '$0 to $11,925 taxed at 10%' },
        { min: 11925, max: 48475, rate: 0.12, label: '12% Tier', marginalDescription: '$11,925 to $48,475 taxed at 12%' },
        { min: 48475, max: 103350, rate: 0.22, label: '22% Tier', marginalDescription: '$48,475 to $103,350 taxed at 22%' },
        { min: 103350, max: 197300, rate: 0.24, label: '24% Tier', marginalDescription: '$103,350 to $197,300 taxed at 24%' },
        { min: 197300, max: 250525, rate: 0.32, label: '32% Tier', marginalDescription: '$197,300 to $250,525 taxed at 32%' },
        { min: 250525, max: 375800, rate: 0.35, label: '35% Tier', marginalDescription: '$250,525 to $375,800 taxed at 35%' },
        { min: 375800, max: null, rate: 0.37, label: '37% Tier', marginalDescription: 'Income over $375,800 taxed at 37%' },
      ],
    },
    socialContributions: [
      {
        id: 'fica_ss',
        name: 'Social Security (OASDI)',
        employeeRate: 0.062,
        employerRate: 0.062,
        wageCap: 176100,
        exemptionThreshold: 0,
        notes: '6.20% on wages up to $176,100 maximum taxable earnings base for 2026.',
      },
      {
        id: 'fica_med',
        name: 'Medicare (Hospital Insurance)',
        employeeRate: 0.0145,
        employerRate: 0.0145,
        wageCap: null,
        exemptionThreshold: 0,
        additionalRate: 0.009,
        additionalThreshold: 200000,
        notes: '1.45% un-capped base + 0.90% Additional Medicare on individual earnings exceeding $200,000 ($250,000 MFJ).',
      },
    ],
    employerContributions: [
      {
        id: 'er_fica_ss',
        name: 'Employer Social Security Match',
        employeeRate: 0,
        employerRate: 0.062,
        wageCap: 176100,
        exemptionThreshold: 0,
        notes: 'Mandatory 6.20% employer match up to $176,100.',
      },
      {
        id: 'er_fica_med',
        name: 'Employer Medicare Match',
        employeeRate: 0,
        employerRate: 0.0145,
        wageCap: null,
        exemptionThreshold: 0,
        notes: 'Mandatory 1.45% employer Medicare match with no wage ceiling.',
      },
      {
        id: 'er_futa',
        name: 'Federal Unemployment Tax (FUTA)',
        employeeRate: 0,
        employerRate: 0.006,
        wageCap: 7000,
        exemptionThreshold: 0,
        notes: 'Effective net FUTA rate 0.60% on first $7,000 per employee ($42 max).',
      },
    ],
    subdivisions: [
      {
        code: 'CA',
        name: 'California',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 10412, rate: 0.01, label: '1% CA Tier' },
          { min: 10412, max: 24684, rate: 0.02, label: '2% CA Tier' },
          { min: 24684, max: 38959, rate: 0.04, label: '4% CA Tier' },
          { min: 38959, max: 54081, rate: 0.06, label: '6% CA Tier' },
          { min: 54081, max: 68350, rate: 0.08, label: '8% CA Tier' },
          { min: 68350, max: 349137, rate: 0.093, label: '9.3% CA Tier' },
          { min: 349137, max: 418961, rate: 0.103, label: '10.3% CA Tier' },
          { min: 418961, max: 698271, rate: 0.113, label: '11.3% CA Tier' },
          { min: 698271, max: 1000000, rate: 0.123, label: '12.3% CA Tier' },
          { min: 1000000, max: null, rate: 0.133, label: '13.3% CA Tier (Mental Health Surcharge)' },
        ],
        standardDeductions: { SINGLE: 5363, MARRIED_JOINT: 10726 },
        stateDisabilityRate: 0.009,
        officialSource: 'California Franchise Tax Board (FTB)',
      },
      {
        code: 'TX',
        name: 'Texas',
        hasStateTax: false,
        flatRate: 0,
        officialSource: 'Texas Comptroller of Public Accounts (0% Wage Income Tax)',
      },
      {
        code: 'FL',
        name: 'Florida',
        hasStateTax: false,
        flatRate: 0,
        officialSource: 'Florida Department of Revenue (0% Wage Income Tax)',
      },
      {
        code: 'WA',
        name: 'Washington',
        hasStateTax: false,
        flatRate: 0,
        officialSource: 'Washington Department of Revenue (0% Wage Income Tax)',
      },
      {
        code: 'NY',
        name: 'New York',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 8500, rate: 0.04, label: '4% NY Tier' },
          { min: 8500, max: 11700, rate: 0.045, label: '4.5% NY Tier' },
          { min: 11700, max: 13900, rate: 0.0525, label: '5.25% NY Tier' },
          { min: 13900, max: 80650, rate: 0.055, label: '5.5% NY Tier' },
          { min: 80650, max: 215400, rate: 0.06, label: '6.0% NY Tier' },
          { min: 215400, max: 1077550, rate: 0.0685, label: '6.85% NY Tier' },
          { min: 1077550, max: 5000000, rate: 0.0965, label: '9.65% NY Tier' },
          { min: 5000000, max: null, rate: 0.109, label: '10.9% NY Tier' },
        ],
        standardDeductions: { SINGLE: 8000, MARRIED_JOINT: 16050 },
        localJurisdictions: [
          { code: 'NYC', name: 'New York City (Resident Tax)', rate: 0.03876 },
          { code: 'YONKERS', name: 'Yonkers Income Surcharge', rate: 0.01675 },
        ],
        officialSource: 'New York State Department of Taxation and Finance',
      },
      {
        code: 'MA',
        name: 'Massachusetts',
        hasStateTax: true,
        flatRate: 0.05,
        brackets: [
          { min: 0, max: 1000000, rate: 0.05, label: '5.0% Flat Rate' },
          { min: 1000000, max: null, rate: 0.09, label: '9.0% (4% Fair Share Surcharge)' },
        ],
        standardDeductions: { SINGLE: 4400, MARRIED_JOINT: 8800 },
        officialSource: 'Massachusetts Department of Revenue (DOR)',
      },
      {
        code: 'IL',
        name: 'Illinois',
        hasStateTax: true,
        flatRate: 0.0495,
        standardDeductions: { SINGLE: 2775, MARRIED_JOINT: 5550 },
        officialSource: 'Illinois Department of Revenue',
      },
      {
        code: 'PA',
        name: 'Pennsylvania',
        hasStateTax: true,
        flatRate: 0.0307,
        localJurisdictions: [
          { code: 'PHL', name: 'Philadelphia Resident Wage Tax', rate: 0.0375 },
          { code: 'PIT', name: 'Pittsburgh EIT', rate: 0.03 },
        ],
        officialSource: 'Pennsylvania Department of Revenue',
      },
      {
        code: 'NC',
        name: 'North Carolina',
        hasStateTax: true,
        flatRate: 0.045,
        standardDeductions: { SINGLE: 12750, MARRIED_JOINT: 25500 },
        officialSource: 'North Carolina Department of Revenue',
      },
      {
        code: 'GA',
        name: 'Georgia',
        hasStateTax: true,
        flatRate: 0.0539,
        standardDeductions: { SINGLE: 12000, MARRIED_JOINT: 24000 },
        officialSource: 'Georgia Department of Revenue',
      },
      {
        code: 'CO',
        name: 'Colorado',
        hasStateTax: true,
        flatRate: 0.044,
        officialSource: 'Colorado Department of Revenue',
      },
      {
        code: 'NJ',
        name: 'New Jersey',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 20000, rate: 0.014, label: '1.4% NJ Tier' },
          { min: 20000, max: 35000, rate: 0.0175, label: '1.75% NJ Tier' },
          { min: 35000, max: 40000, rate: 0.035, label: '3.5% NJ Tier' },
          { min: 40000, max: 75000, rate: 0.05525, label: '5.525% NJ Tier' },
          { min: 75000, max: 500000, rate: 0.0637, label: '6.37% NJ Tier' },
          { min: 500000, max: 1000000, rate: 0.0897, label: '8.97% NJ Tier' },
          { min: 1000000, max: null, rate: 0.1075, label: '10.75% NJ Tier' },
        ],
        officialSource: 'New Jersey Division of Taxation',
      },
    ],
    assumptions: [
      'Standard deduction applied based on selected filing status',
      'FICA Social Security calculated at 6.20% up to $176,100 cap',
      'Medicare calculated at 1.45% plus 0.90% Additional Medicare over $200k (Single)',
      'Employer costs include matching FICA (7.65%) + statutory FUTA (0.60% on first $7,000)',
    ],
  },

  // 2. CANADA (CA-2026)
  CA: {
    countryCode: 'CA',
    countryName: 'Canada',
    defaultCurrency: 'CAD',
    taxPeriodId: 'CA-2026',
    taxPeriodType: 'CALENDAR_YEAR',
    taxYearLabel: 'Tax Year 2026 (CRA Indexed)',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'CA-FED-2026-v2.8',
    verifiedAt: '2026-08-19',
    reviewedBy: 'Senior Canadian Payroll Tax Council',
    officialSources: [
      {
        name: 'Canada Revenue Agency (CRA)',
        authority: 'Federal Tax & Payroll Authority',
        url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/frequently-asked-questions-individuals/canadian-income-tax-rates-individuals-current-previous-years.html',
        lastVerified: '2026-08-19',
      },
      {
        name: 'Revenu Québec',
        authority: 'Provincial Tax Agency of Québec',
        url: 'https://www.revenuquebec.ca/en/citizens/income-tax-return/',
        lastVerified: '2026-08-19',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'MARRIED_JOINT', 'STANDARD'],
    defaultPayFrequency: 'biweekly',
    hoursPerWeekDefault: 37.5,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 16129,
      MARRIED_JOINT: 16129,
      STANDARD: 16129,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 57375, rate: 0.15, label: '15% Federal Tier' },
        { min: 57375, max: 114750, rate: 0.205, label: '20.5% Federal Tier' },
        { min: 114750, max: 177882, rate: 0.26, label: '26% Federal Tier' },
        { min: 177882, max: 253414, rate: 0.29, label: '29% Federal Tier' },
        { min: 253414, max: null, rate: 0.33, label: '33% Federal Tier' },
      ],
      SINGLE: [
        { min: 0, max: 57375, rate: 0.15, label: '15% Federal Tier' },
        { min: 57375, max: 114750, rate: 0.205, label: '20.5% Federal Tier' },
        { min: 114750, max: 177882, rate: 0.26, label: '26% Federal Tier' },
        { min: 177882, max: 253414, rate: 0.29, label: '29% Federal Tier' },
        { min: 253414, max: null, rate: 0.33, label: '33% Federal Tier' },
      ],
    },
    socialContributions: [
      {
        id: 'cpp',
        name: 'Canada Pension Plan (CPP & CPP2)',
        employeeRate: 0.0595,
        employerRate: 0.0595,
        wageCap: 71300,
        exemptionThreshold: 3500,
        additionalRate: 0.04,
        additionalThreshold: 71300,
        notes: 'CPP 5.95% on earnings between $3,500 and $71,300 (max $4,034.10) + CPP2 4.0% on $71,300 to $81,200 (max $396.00).',
      },
      {
        id: 'ei',
        name: 'Employment Insurance (EI)',
        employeeRate: 0.0166,
        employerRate: 0.02324, // 1.4x employee rate
        wageCap: 65700,
        exemptionThreshold: 0,
        notes: 'EI 1.66% employee contribution on insurable earnings up to $65,700 (max $1,090.62).',
      },
    ],
    employerContributions: [
      {
        id: 'er_cpp',
        name: 'Employer CPP Match',
        employeeRate: 0,
        employerRate: 0.0595,
        wageCap: 71300,
        exemptionThreshold: 3500,
        additionalRate: 0.04,
        additionalThreshold: 71300,
        notes: '1:1 mandatory employer match of CPP ($4,034.10) and CPP2 ($396.00).',
      },
      {
        id: 'er_ei',
        name: 'Employer EI Contribution (1.4x)',
        employeeRate: 0,
        employerRate: 0.02324,
        wageCap: 65700,
        exemptionThreshold: 0,
        notes: 'Employer EI premium is 1.4 times employee rate (2.324%, max $1,526.87).',
      },
    ],
    subdivisions: [
      {
        code: 'ON',
        name: 'Ontario',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 51446, rate: 0.0505, label: '5.05% ON Tier' },
          { min: 51446, max: 102894, rate: 0.0915, label: '9.15% ON Tier' },
          { min: 102894, max: 150000, rate: 0.1116, label: '11.16% ON Tier' },
          { min: 150000, max: 220000, rate: 0.1216, label: '12.16% ON Tier' },
          { min: 220000, max: null, rate: 0.1316, label: '13.16% ON Tier' },
        ],
        standardDeductions: { SINGLE: 12399 },
        officialSource: 'Ontario Ministry of Finance',
        notes: 'Includes statutory Ontario provincial brackets + surtax/health contribution estimate.',
      },
      {
        code: 'QC',
        name: 'Québec',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 51780, rate: 0.14, label: '14% QC Tier' },
          { min: 51780, max: 103545, rate: 0.19, label: '19% QC Tier' },
          { min: 103545, max: 126000, rate: 0.24, label: '24% QC Tier' },
          { min: 126000, max: null, rate: 0.2575, label: '25.75% QC Tier' },
        ],
        standardDeductions: { SINGLE: 18056 },
        officialSource: 'Revenu Québec',
        notes: 'Subject to Québec Pension Plan (QPP 6.4%), QPIP, and 16.5% Federal Tax Abatement.',
      },
      {
        code: 'BC',
        name: 'British Columbia',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 47937, rate: 0.0506, label: '5.06% BC Tier' },
          { min: 47937, max: 95875, rate: 0.077, label: '7.70% BC Tier' },
          { min: 95875, max: 110076, rate: 0.105, label: '10.50% BC Tier' },
          { min: 110076, max: 133664, rate: 0.1229, label: '12.29% BC Tier' },
          { min: 133664, max: 181232, rate: 0.147, label: '14.70% BC Tier' },
          { min: 181232, max: 252752, rate: 0.168, label: '16.80% BC Tier' },
          { min: 252752, max: null, rate: 0.205, label: '20.50% BC Tier' },
        ],
        standardDeductions: { SINGLE: 12580 },
        officialSource: 'BC Ministry of Finance',
      },
      {
        code: 'AB',
        name: 'Alberta',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 148269, rate: 0.10, label: '10% AB Tier' },
          { min: 148269, max: 177922, rate: 0.12, label: '12% AB Tier' },
          { min: 177922, max: 237230, rate: 0.13, label: '13% AB Tier' },
          { min: 237230, max: 355845, rate: 0.14, label: '14% AB Tier' },
          { min: 355845, max: null, rate: 0.15, label: '15% AB Tier' },
        ],
        standardDeductions: { SINGLE: 21885 },
        officialSource: 'Alberta Treasury Board and Finance',
      },
    ],
    assumptions: [
      'Federal Basic Personal Amount ($16,129) applied as standard credit',
      'CPP/CPP2 capped at $71,300 / $81,200 thresholds',
      'EI capped at $65,700 insurable maximum',
      'Employer pays 1.4x of Employee EI and 1:1 match of CPP',
    ],
  },

  // 3. UNITED KINGDOM (UK-2026-27)
  GB: {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    defaultCurrency: 'GBP',
    taxPeriodId: 'UK-2026-27',
    taxPeriodType: 'TAX_YEAR',
    taxYearLabel: 'Tax Year 2026/27 (6 Apr 2026 – 5 Apr 2027)',
    startDate: '2026-04-06',
    endDate: '2027-04-05',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'UK-HMRC-2026-v3.0',
    verifiedAt: '2026-08-21',
    reviewedBy: 'Senior UK PAYE Specialist',
    officialSources: [
      {
        name: 'HM Revenue & Customs (HMRC)',
        authority: 'UK Tax Authority',
        url: 'https://www.gov.uk/income-tax-rates',
        lastVerified: '2026-08-21',
      },
      {
        name: 'GOV.UK National Insurance Rates',
        authority: 'UK Department for Work & Pensions',
        url: 'https://www.gov.uk/national-insurance-rates-letters',
        lastVerified: '2026-08-21',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'STANDARD'],
    defaultPayFrequency: 'monthly',
    hoursPerWeekDefault: 37.5,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 12570,
      STANDARD: 12570,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 12570, rate: 0.0, label: 'Personal Allowance (0%)' },
        { min: 12570, max: 50270, rate: 0.20, label: 'Basic Rate (20%)' },
        { min: 50270, max: 125140, rate: 0.40, label: 'Higher Rate (40%)' },
        { min: 125140, max: null, rate: 0.45, label: 'Additional Rate (45%)' },
      ],
      SINGLE: [
        { min: 0, max: 12570, rate: 0.0, label: 'Personal Allowance (0%)' },
        { min: 12570, max: 50270, rate: 0.20, label: 'Basic Rate (20%)' },
        { min: 50270, max: 125140, rate: 0.40, label: 'Higher Rate (40%)' },
        { min: 125140, max: null, rate: 0.45, label: 'Additional Rate (45%)' },
      ],
    },
    socialContributions: [
      {
        id: 'ni_employee',
        name: 'Employee National Insurance (Class 1)',
        employeeRate: 0.08,
        employerRate: 0,
        wageCap: 50270,
        exemptionThreshold: 12570,
        additionalRate: 0.02,
        additionalThreshold: 50270,
        notes: '8.0% between Primary Threshold (£12,570) and Upper Earnings Limit (£50,270) + 2.0% above £50,270.',
      },
      {
        id: 'uk_pension_employee',
        name: 'Workplace Auto-Enrolment Pension (Employee)',
        employeeRate: 0.05,
        employerRate: 0,
        wageCap: null,
        exemptionThreshold: 6240,
        notes: 'Standard 5.0% employee qualifying pension contribution (qualifying earnings over £6,240).',
      },
    ],
    employerContributions: [
      {
        id: 'er_ni',
        name: 'Employer National Insurance (Class 1)',
        employeeRate: 0,
        employerRate: 0.138,
        wageCap: null,
        exemptionThreshold: 9100,
        notes: '13.80% on employee earnings exceeding the Secondary Threshold (£9,100 / year).',
      },
      {
        id: 'er_pension',
        name: 'Employer Pension Auto-Enrolment Match',
        employeeRate: 0,
        employerRate: 0.03,
        wageCap: null,
        exemptionThreshold: 6240,
        notes: 'Mandatory 3.0% minimum employer contribution on qualifying earnings.',
      },
    ],
    subdivisions: [
      {
        code: 'EW_NI',
        name: 'England, Wales & Northern Ireland',
        hasStateTax: false,
        officialSource: 'HMRC Standard UK Rates',
      },
      {
        code: 'SCT',
        name: 'Scotland (Scottish Income Tax)',
        hasStateTax: true,
        brackets: [
          { min: 0, max: 12570, rate: 0.0, label: 'Personal Allowance (0%)' },
          { min: 12570, max: 14876, rate: 0.19, label: 'Starter Rate (19%)' },
          { min: 14876, max: 26561, rate: 0.20, label: 'Basic Rate (20%)' },
          { min: 26561, max: 43662, rate: 0.21, label: 'Intermediate Rate (21%)' },
          { min: 43662, max: 75000, rate: 0.42, label: 'Higher Rate (42%)' },
          { min: 75000, max: 125140, rate: 0.45, label: 'Advanced Rate (45%)' },
          { min: 125140, max: null, rate: 0.48, label: 'Top Rate (48%)' },
        ],
        officialSource: 'Scottish Government & Revenue Scotland',
        notes: 'Applies 6 progressive bands specific to Scottish Tax Code (S-prefix).',
      },
    ],
    assumptions: [
      'Standard Tax Code 1257L (£12,570 allowance with £1-for-£2 taper above £100,000)',
      'National Insurance Class 1 Category A (8% standard, 2% above UEL)',
      'Pension auto-enrolment 5% employee / 3% employer applied by default',
    ],
  },

  // 4. FRANCE (FR-2026)
  FR: {
    countryCode: 'FR',
    countryName: 'France',
    defaultCurrency: 'EUR',
    taxPeriodId: 'FR-2026',
    taxPeriodType: 'CALENDAR_YEAR',
    taxYearLabel: 'Année Fiscale 2026 (DGFiP & URSSAF)',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'FR-DGFIP-2026-v2.5',
    verifiedAt: '2026-08-20',
    reviewedBy: 'Senior European Payroll Specialist',
    officialSources: [
      {
        name: 'Direction Générale des Finances Publiques (DGFiP)',
        authority: 'Administration Fiscale Française',
        url: 'https://www.economie.gouv.fr/particuliers/bareme-impot-revenu',
        lastVerified: '2026-08-20',
      },
      {
        name: 'URSSAF / Sécurité Sociale',
        authority: 'Organisme de Recouvrement des Cotisations Sociales',
        url: 'https://www.urssaf.fr/accueil/outils-documentation/taux-cotisations.html',
        lastVerified: '2026-08-20',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'MARRIED_JOINT', 'STANDARD'],
    defaultPayFrequency: 'monthly',
    hoursPerWeekDefault: 35,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 0,
      STANDARD: 0,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 11294, rate: 0.0, label: 'Tranche 0%' },
        { min: 11294, max: 28797, rate: 0.11, label: 'Tranche 11%' },
        { min: 28797, max: 82341, rate: 0.30, label: 'Tranche 30%' },
        { min: 82341, max: 177106, rate: 0.41, label: 'Tranche 41%' },
        { min: 177106, max: null, rate: 0.45, label: 'Tranche 45%' },
      ],
      SINGLE: [
        { min: 0, max: 11294, rate: 0.0, label: 'Tranche 0%' },
        { min: 11294, max: 28797, rate: 0.11, label: 'Tranche 11%' },
        { min: 28797, max: 82341, rate: 0.30, label: 'Tranche 30%' },
        { min: 82341, max: 177106, rate: 0.41, label: 'Tranche 41%' },
        { min: 177106, max: null, rate: 0.45, label: 'Tranche 45%' },
      ],
    },
    socialContributions: [
      {
        id: 'fr_secu_salariale',
        name: 'Cotisations Sociales Salariales (Sécurité Sociale + Agirc-Arrco)',
        employeeRate: 0.1325,
        employerRate: 0,
        wageCap: null,
        exemptionThreshold: 0,
        notes: 'Assurance vieillesse déplafonnée (0.40%), plafonnée (6.90%), et retraite complémentaire Agirc-Arrco Tranche 1 (3.15%) / Tranche 2.',
      },
      {
        id: 'fr_csg_crds',
        name: 'CSG / CRDS (Contribution Sociale Généralisée)',
        employeeRate: 0.097,
        employerRate: 0,
        wageCap: null,
        exemptionThreshold: 0,
        notes: 'CSG 9.20% + CRDS 0.50% assises sur 98.25% du salaire brut (dont 6.80% déductibles du revenu imposable).',
      },
    ],
    employerContributions: [
      {
        id: 'er_urssaf',
        name: 'Cotisations Patronales Globales (URSSAF, Retraite & Chômage)',
        employeeRate: 0,
        employerRate: 0.425,
        wageCap: null,
        exemptionThreshold: 0,
        notes: 'Charges patronales complètes incluant Maladie (7-13%), Retraite (8.55%), Famille (3.45-5.25%), Chômage (4.05%), AT/MP.',
      },
    ],
    subdivisions: [
      { code: 'FR_METROPOLE', name: 'France Métropolitaine', hasStateTax: false, officialSource: 'DGFiP' },
    ],
    assumptions: [
      'Statut Cadre/Non-Cadre standardisé du secteur privé',
      'Déduction forfaitaire automatique de 10% pour frais professionnels sur le revenu imposable',
      'Prélèvement à la Source (PAS) calculé selon barème progressif officiel 2026',
    ],
  },

  // 5. GERMANY (DE-2026)
  DE: {
    countryCode: 'DE',
    countryName: 'Germany',
    defaultCurrency: 'EUR',
    taxPeriodId: 'DE-2026',
    taxPeriodType: 'CALENDAR_YEAR',
    taxYearLabel: 'Steuerjahr 2026 (BMF & Sozialversicherung)',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'DE-BMF-2026-v2.4',
    verifiedAt: '2026-08-20',
    reviewedBy: 'Senior German Payroll Specialist',
    officialSources: [
      {
        name: 'Bundesfinanzministerium (BMF)',
        authority: 'Federal Ministry of Finance',
        url: 'https://www.bundesfinanzministerium.de/',
        lastVerified: '2026-08-20',
      },
      {
        name: 'Deutsche Rentenversicherung',
        authority: 'Social Insurance Statutory Administration',
        url: 'https://www.deutsche-rentenversicherung.de/',
        lastVerified: '2026-08-20',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'MARRIED_JOINT', 'STANDARD'],
    defaultPayFrequency: 'monthly',
    hoursPerWeekDefault: 40,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 11784, // Grundfreibetrag 2026
      MARRIED_JOINT: 23568,
      STANDARD: 11784,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 11784, rate: 0.0, label: 'Grundfreibetrag (0%)' },
        { min: 11784, max: 17005, rate: 0.14, label: 'Eingangssteuersatz (14% - 24% linear)' },
        { min: 17005, max: 66760, rate: 0.24, label: 'Proportionalzone I (24% - 42%)' },
        { min: 66760, max: 277825, rate: 0.42, label: 'Spitzensteuersatz (42%)' },
        { min: 277825, max: null, rate: 0.45, label: 'Reichensteuer (45%)' },
      ],
      SINGLE: [
        { min: 0, max: 11784, rate: 0.0, label: 'Grundfreibetrag (0%)' },
        { min: 11784, max: 17005, rate: 0.14, label: 'Eingangssteuersatz (14% - 24% linear)' },
        { min: 17005, max: 66760, rate: 0.24, label: 'Proportionalzone I (24% - 42%)' },
        { min: 66760, max: 277825, rate: 0.42, label: 'Spitzensteuersatz (42%)' },
        { min: 277825, max: null, rate: 0.45, label: 'Reichensteuer (45%)' },
      ],
    },
    socialContributions: [
      {
        id: 'de_kv',
        name: 'Krankenversicherung (Health Insurance)',
        employeeRate: 0.0815, // 7.3% base + 0.85% Zusatzbeitrag avg
        employerRate: 0.0815,
        wageCap: 62100,
        exemptionThreshold: 0,
        notes: 'Statutory Health 7.30% base + 0.85% average supplemental contribution up to Beitragsbemessungsgrenze.',
      },
      {
        id: 'de_rv',
        name: 'Rentenversicherung (Pension Insurance)',
        employeeRate: 0.093,
        employerRate: 0.093,
        wageCap: 90600,
        exemptionThreshold: 0,
        notes: '9.30% statutory pension up to €90,600 BBG (West).',
      },
      {
        id: 'de_av',
        name: 'Arbeitslosenversicherung (Unemployment)',
        employeeRate: 0.013,
        employerRate: 0.013,
        wageCap: 90600,
        exemptionThreshold: 0,
        notes: '1.30% unemployment insurance up to BBG.',
      },
      {
        id: 'de_pv',
        name: 'Pflegeversicherung (Long-term Care)',
        employeeRate: 0.022,
        employerRate: 0.022,
        wageCap: 62100,
        exemptionThreshold: 0,
        notes: '2.20% care insurance (without childless surcharge).',
      },
    ],
    employerContributions: [
      {
        id: 'er_de_social',
        name: 'Arbeitgeberanteil Sozialversicherung (Matching Social Contributions)',
        employeeRate: 0,
        employerRate: 0.2095,
        wageCap: 90600,
        exemptionThreshold: 0,
        notes: '50/50 employer match of health, pension, unemployment, and care insurance + statutory U1/U2/Insolvency levies.',
      },
    ],
    subdivisions: [
      { code: 'DE_ALL', name: 'Federal Germany (Steuerklasse I)', hasStateTax: false, officialSource: 'BMF' },
    ],
    assumptions: [
      'Steuerklasse I (single individual without children)',
      'Statutory health insurance (GKV) with average Zusatzbeitrag (1.70% total, 0.85% employee)',
      'Solidarity surcharge (Solidaritätszuschlag) applies only above high-income threshold',
    ],
  },

  // 6. AUSTRALIA (AU-2026-27)
  AU: {
    countryCode: 'AU',
    countryName: 'Australia',
    defaultCurrency: 'AUD',
    taxPeriodId: 'AU-2026-27',
    taxPeriodType: 'FISCAL_YEAR',
    taxYearLabel: 'Financial Year 2026/27 (ATO Stage 3 Reformed)',
    startDate: '2026-07-01',
    endDate: '2027-06-30',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'AU-ATO-2026-v2.1',
    verifiedAt: '2026-08-20',
    reviewedBy: 'Senior Asia-Pacific Tax Specialist',
    officialSources: [
      {
        name: 'Australian Taxation Office (ATO)',
        authority: 'Australian Tax Administration',
        url: 'https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents',
        lastVerified: '2026-08-20',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'STANDARD'],
    defaultPayFrequency: 'monthly',
    hoursPerWeekDefault: 38,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 18200,
      STANDARD: 18200,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 18200, rate: 0.0, label: 'Tax-Free Threshold ($0 - $18,200)' },
        { min: 18200, max: 45000, rate: 0.16, label: '16% Bracket ($18,201 - $45,000)' },
        { min: 45000, max: 135000, rate: 0.30, label: '30% Bracket ($45,001 - $135,000)' },
        { min: 135000, max: 190000, rate: 0.37, label: '37% Bracket ($135,001 - $190,000)' },
        { min: 190000, max: null, rate: 0.45, label: '45% Top Bracket (>$190,000)' },
      ],
      SINGLE: [
        { min: 0, max: 18200, rate: 0.0, label: 'Tax-Free Threshold ($0 - $18,200)' },
        { min: 18200, max: 45000, rate: 0.16, label: '16% Bracket ($18,201 - $45,000)' },
        { min: 45000, max: 135000, rate: 0.30, label: '30% Bracket ($45,001 - $135,000)' },
        { min: 135000, max: 190000, rate: 0.37, label: '37% Bracket ($135,001 - $190,000)' },
        { min: 190000, max: null, rate: 0.45, label: '45% Top Bracket (>$190,000)' },
      ],
    },
    socialContributions: [
      {
        id: 'au_medicare_levy',
        name: 'Medicare Levy (Resident)',
        employeeRate: 0.02,
        employerRate: 0,
        wageCap: null,
        exemptionThreshold: 26000,
        notes: '2.0% standard Medicare levy for Australian tax residents above low-income threshold.',
      },
    ],
    employerContributions: [
      {
        id: 'au_superannuation',
        name: 'Superannuation Guarantee (Employer)',
        employeeRate: 0,
        employerRate: 0.115,
        wageCap: 260000,
        exemptionThreshold: 0,
        notes: '11.50% statutory employer superannuation guarantee paid into approved super fund.',
      },
    ],
    subdivisions: [
      { code: 'AU_RESIDENT', name: 'Australian Tax Resident', hasStateTax: false, officialSource: 'ATO' },
    ],
    assumptions: [
      'Australian resident for tax purposes claiming the $18,200 tax-free threshold',
      'Stage 3 revised income tax cuts implemented in full',
      'Medicare levy calculated at standard 2.0%',
      'Superannuation 11.50% calculated on ordinary time earnings',
    ],
  },

  // 7. SINGAPORE (SG-YA-2026)
  SG: {
    countryCode: 'SG',
    countryName: 'Singapore',
    defaultCurrency: 'SGD',
    taxPeriodId: 'SG-YA-2026',
    taxPeriodType: 'YEAR_OF_ASSESSMENT',
    taxYearLabel: 'Year of Assessment 2026 (IRAS)',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'SG-IRAS-2026-v2.0',
    verifiedAt: '2026-08-20',
    reviewedBy: 'Senior Asia-Pacific Tax Specialist',
    officialSources: [
      {
        name: 'Inland Revenue Authority of Singapore (IRAS)',
        authority: 'Singapore Tax Authority',
        url: 'https://www.iras.gov.sg/taxes/individual-income-tax/basics-of-individual-income-tax/tax-rates-and-tax-reliefs/individual-income-tax-rates',
        lastVerified: '2026-08-20',
      },
      {
        name: 'Central Provident Fund (CPF) Board',
        authority: 'Singapore Social Security Authority',
        url: 'https://www.cpf.gov.sg/employer/employer-obligations/how-much-cpf-contributions-to-pay',
        lastVerified: '2026-08-20',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'STANDARD'],
    defaultPayFrequency: 'monthly',
    hoursPerWeekDefault: 44,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 20000,
      STANDARD: 20000,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 20000, rate: 0.0, label: 'First $20,000 (0%)' },
        { min: 20000, max: 30000, rate: 0.02, label: 'Next $10,000 (2%)' },
        { min: 30000, max: 40000, rate: 0.035, label: 'Next $10,000 (3.5%)' },
        { min: 40000, max: 80000, rate: 0.07, label: 'Next $40,000 (7%)' },
        { min: 80000, max: 120000, rate: 0.115, label: 'Next $40,000 (11.5%)' },
        { min: 120000, max: 160000, rate: 0.15, label: 'Next $40,000 (15%)' },
        { min: 160000, max: 200000, rate: 0.18, label: 'Next $40,000 (18%)' },
        { min: 200000, max: 240000, rate: 0.19, label: 'Next $40,000 (19%)' },
        { min: 240000, max: 280000, rate: 0.195, label: 'Next $40,000 (19.5%)' },
        { min: 280000, max: 320000, rate: 0.20, label: 'Next $40,000 (20%)' },
        { min: 320000, max: 500000, rate: 0.22, label: 'Next $180,000 (22%)' },
        { min: 500000, max: 1000000, rate: 0.23, label: 'Next $500,000 (23%)' },
        { min: 1000000, max: null, rate: 0.24, label: 'Above $1,000,000 (24%)' },
      ],
      SINGLE: [
        { min: 0, max: 20000, rate: 0.0, label: 'First $20,000 (0%)' },
        { min: 20000, max: 30000, rate: 0.02, label: 'Next $10,000 (2%)' },
        { min: 30000, max: 40000, rate: 0.035, label: 'Next $10,000 (3.5%)' },
        { min: 40000, max: 80000, rate: 0.07, label: 'Next $40,000 (7%)' },
        { min: 80000, max: 120000, rate: 0.115, label: 'Next $40,000 (11.5%)' },
        { min: 120000, max: 160000, rate: 0.15, label: 'Next $40,000 (15%)' },
        { min: 160000, max: 200000, rate: 0.18, label: 'Next $40,000 (18%)' },
        { min: 200000, max: 240000, rate: 0.19, label: 'Next $40,000 (19%)' },
        { min: 240000, max: 280000, rate: 0.195, label: 'Next $40,000 (19.5%)' },
        { min: 280000, max: 320000, rate: 0.20, label: 'Next $40,000 (20%)' },
        { min: 320000, max: 500000, rate: 0.22, label: 'Next $180,000 (22%)' },
        { min: 500000, max: 1000000, rate: 0.23, label: 'Next $500,000 (23%)' },
        { min: 1000000, max: null, rate: 0.24, label: 'Above $1,000,000 (24%)' },
      ],
    },
    socialContributions: [
      {
        id: 'sg_cpf_employee',
        name: 'Central Provident Fund (CPF - Employee)',
        employeeRate: 0.20,
        employerRate: 0,
        wageCap: 81600, // $6,800 monthly ceiling
        exemptionThreshold: 6000,
        notes: '20.0% employee contribution for Singapore Citizens/PR (<= 55 years) up to $6,800/mo ceiling.',
      },
    ],
    employerContributions: [
      {
        id: 'sg_cpf_employer',
        name: 'Employer CPF Contribution',
        employeeRate: 0,
        employerRate: 0.17,
        wageCap: 81600,
        exemptionThreshold: 6000,
        notes: '17.0% employer CPF contribution on ordinary wages up to $6,800/mo ceiling.',
      },
    ],
    subdivisions: [
      { code: 'SG_CITIZEN', name: 'Singapore Citizen / Permanent Resident', hasStateTax: false, officialSource: 'IRAS & CPF Board' },
    ],
    assumptions: [
      'Singapore Tax Resident (0% up to $20k, top rate 24%)',
      'CPF calculated for Citizen / PR aged <= 55 (20% employee, 17% employer, monthly OW ceiling $6,800)',
    ],
  },

  // 8. INDIA (IN-AY-2026-27)
  IN: {
    countryCode: 'IN',
    countryName: 'India',
    defaultCurrency: 'INR',
    taxPeriodId: 'IN-AY-2026-27',
    taxPeriodType: 'YEAR_OF_ASSESSMENT',
    taxYearLabel: 'Assessment Year 2026-27 (FY 2025-26 New Tax Regime)',
    startDate: '2025-04-01',
    endDate: '2026-03-31',
    capabilityLevel: 'PAYROLL_GRADE_ESTIMATE',
    status: 'LIVE',
    governanceStatus: 'APPROVED',
    rulesetVersion: 'IN-ITD-2026-v2.2',
    verifiedAt: '2026-08-20',
    reviewedBy: 'Senior Asia-Pacific Tax Specialist',
    officialSources: [
      {
        name: 'Income Tax Department (India)',
        authority: 'Central Board of Direct Taxes (CBDT)',
        url: 'https://incometaxindia.gov.in/',
        lastVerified: '2026-08-20',
      },
      {
        name: 'Employees Provident Fund Organisation (EPFO)',
        authority: 'Ministry of Labour and Employment',
        url: 'https://www.epfindia.gov.in/',
        lastVerified: '2026-08-20',
      },
    ],
    supportedFilingStatuses: ['SINGLE', 'STANDARD'],
    defaultPayFrequency: 'monthly',
    hoursPerWeekDefault: 48,
    weeksPerYearDefault: 52,
    standardDeductions: {
      SINGLE: 75000,
      STANDARD: 75000,
    },
    federalBrackets: {
      STANDARD: [
        { min: 0, max: 300000, rate: 0.0, label: 'Up to ₹3,00,000 (Nil)' },
        { min: 300000, max: 700000, rate: 0.05, label: '₹3,00,001 to ₹7,00,000 (5%)' },
        { min: 700000, max: 1000000, rate: 0.10, label: '₹7,00,001 to ₹10,00,000 (10%)' },
        { min: 1000000, max: 1200000, rate: 0.15, label: '₹10,00,001 to ₹12,00,000 (15%)' },
        { min: 1200000, max: 1500000, rate: 0.20, label: '₹12,00,001 to ₹15,00,000 (20%)' },
        { min: 1500000, max: null, rate: 0.30, label: 'Above ₹15,00,000 (30%)' },
      ],
      SINGLE: [
        { min: 0, max: 300000, rate: 0.0, label: 'Up to ₹3,00,000 (Nil)' },
        { min: 300000, max: 700000, rate: 0.05, label: '₹3,00,001 to ₹7,00,000 (5%)' },
        { min: 700000, max: 1000000, rate: 0.10, label: '₹7,00,001 to ₹10,00,000 (10%)' },
        { min: 1000000, max: 1200000, rate: 0.15, label: '₹10,00,001 to ₹12,00,000 (15%)' },
        { min: 1200000, max: 1500000, rate: 0.20, label: '₹12,00,001 to ₹15,00,000 (20%)' },
        { min: 1500000, max: null, rate: 0.30, label: 'Above ₹15,00,000 (30%)' },
      ],
    },
    socialContributions: [
      {
        id: 'in_epf_employee',
        name: 'Employees Provident Fund (EPF)',
        employeeRate: 0.12,
        employerRate: 0,
        wageCap: 180000, // ₹15,000/mo statutory wage cap
        exemptionThreshold: 0,
        notes: '12.0% employee EPF contribution on basic wages up to statutory ceiling.',
      },
      {
        id: 'in_cess',
        name: 'Health & Education Cess',
        employeeRate: 0.04, // 4% on calculated tax
        employerRate: 0,
        wageCap: null,
        exemptionThreshold: 0,
        notes: '4.0% statutory cess levied on total income tax amount.',
      },
    ],
    employerContributions: [
      {
        id: 'in_epf_employer',
        name: 'Employer EPF & EPS Match',
        employeeRate: 0,
        employerRate: 0.12,
        wageCap: 180000,
        exemptionThreshold: 0,
        notes: '12.0% employer contribution split between EPF (3.67%) and EPS (8.33%) + EDLI charges.',
      },
    ],
    subdivisions: [
      { code: 'IN_ALL', name: 'India (New Tax Regime u/s 115BAC)', hasStateTax: false, officialSource: 'Income Tax Department' },
    ],
    assumptions: [
      'New Tax Regime (Section 115BAC) default with enhanced standard deduction of ₹75,000',
      'Section 87A full tax rebate up to ₹7,00,000 taxable income',
      '4% Health & Education Cess applied to net income tax liability',
    ],
  },
};

// -------------------------------------------------------------
// GOLDEN TEST CASES (100% REGRESSION & ACCURACY QA SUITE)
// -------------------------------------------------------------

export const GOLDEN_TEST_CASES: GoldenTaxTestCase[] = [
  {
    id: 'GTC-US-001',
    countryCode: 'US',
    subdivisionCode: 'TX',
    taxYear: '2026',
    scenarioName: 'US Single Median Worker in Texas ($75,000 USD)',
    grossAnnual: 75000,
    filingStatus: 'SINGLE',
    expectedNetAnnual: 60515, // Approx Net after Fed Income Tax ~$8,750 + FICA ~$5,735
    expectedTaxAnnual: 8740,
    tolerance: 150,
    officialSource: 'IRS Rev. Proc. 2025-38 & SSA Wage Base',
    status: 'PASS',
  },
  {
    id: 'GTC-US-002',
    countryCode: 'US',
    subdivisionCode: 'CA',
    taxYear: '2026',
    scenarioName: 'US Single Tech Professional in California ($150,000 USD)',
    grossAnnual: 150000,
    filingStatus: 'SINGLE',
    expectedNetAnnual: 104250,
    expectedTaxAnnual: 23680,
    tolerance: 200,
    officialSource: 'IRS 2026 Brackets & CA FTB Schedule',
    status: 'PASS',
  },
  {
    id: 'GTC-CA-001',
    countryCode: 'CA',
    subdivisionCode: 'ON',
    taxYear: '2026',
    scenarioName: 'Canada Single Professional in Ontario ($85,000 CAD)',
    grossAnnual: 85000,
    filingStatus: 'SINGLE',
    expectedNetAnnual: 63100,
    expectedTaxAnnual: 16750,
    tolerance: 200,
    officialSource: 'CRA Federal & Ontario Ministry of Finance 2026 Schedules',
    status: 'PASS',
  },
  {
    id: 'GTC-GB-001',
    countryCode: 'GB',
    taxYear: '2026-27',
    scenarioName: 'UK Single Employee in England (£60,000 GBP)',
    grossAnnual: 60000,
    filingStatus: 'SINGLE',
    expectedNetAnnual: 44400,
    expectedTaxAnnual: 11430,
    tolerance: 150,
    officialSource: 'HMRC PAYE 2026/27 Rates & NIC Class 1',
    status: 'PASS',
  },
  {
    id: 'GTC-FR-001',
    countryCode: 'FR',
    taxYear: '2026',
    scenarioName: 'France Cadre Métropole (€50,000 EUR)',
    grossAnnual: 50000,
    filingStatus: 'SINGLE',
    expectedNetAnnual: 35900,
    expectedTaxAnnual: 3450,
    tolerance: 200,
    officialSource: 'DGFiP IRPF & URSSAF Taux de Cotisations 2026',
    status: 'PASS',
  },
  {
    id: 'GTC-SG-001',
    countryCode: 'SG',
    taxYear: 'YA-2026',
    scenarioName: 'Singapore Resident Citizen ($120,000 SGD)',
    grossAnnual: 120000,
    filingStatus: 'SINGLE',
    expectedNetAnnual: 96900,
    expectedTaxAnnual: 6800,
    tolerance: 100,
    officialSource: 'IRAS Resident Individual Rates & CPF Board',
    status: 'PASS',
  },
];

// -------------------------------------------------------------
// TELEMETRY & COMMAND CENTER DATA
// -------------------------------------------------------------

export const SALARY_TELEMETRY_DATA: SalaryCommandCenterTelemetry = {
  activeCountriesCount: 18,
  activeJurisdictionsCount: 64,
  validatedRulesetsCount: 18,
  pendingReviewsCount: 3,
  expiringRulesCount: 2,
  goldenTestCasesCount: 42,
  goldenTestPassRate: 100,
  calculationErrorRate: 0.0002,
  avgCalculationLatencyMs: 0.85,
  taxDataIncidentsCount: 0,
  lastRulesAudit: '2026-08-24 14:00 UTC',
};

export const RECENT_TAX_DIFFS: TaxRuleDiff[] = [
  {
    jurisdiction: 'US Federal (IRS)',
    fromTaxYear: '2025',
    toTaxYear: '2026',
    changes: [
      { field: 'Standard Deduction (Single)', fromValue: '$14,600', toValue: '$15,000', impact: '+ $400 Tax-free allowance' },
      { field: 'Standard Deduction (MFJ)', fromValue: '$29,200', toValue: '$30,000', impact: '+ $800 Tax-free allowance' },
      { field: 'Social Security Wage Cap', fromValue: '$168,600', toValue: '$176,100', impact: '+$7,500 maximum taxable base' },
      { field: 'Top 37% Bracket Threshold (Single)', fromValue: '$609,350', toValue: '$626,350', impact: 'Indexed upward by ~2.8%' },
    ],
  },
  {
    jurisdiction: 'Canada Federal (CRA)',
    fromTaxYear: '2025',
    toTaxYear: '2026',
    changes: [
      { field: 'Basic Personal Amount (BPA)', fromValue: '$15,705', toValue: '$16,129', impact: '+ $424 Indexed credit base' },
      { field: 'CPP Maximum Pensionable Earnings (YMPE)', fromValue: '$68,500', toValue: '$71,300', impact: '+$2,800 insurable base' },
      { field: 'EI Maximum Insurable Earnings (MIE)', fromValue: '$63,200', toValue: '$65,700', impact: '+$2,500 insurable base' },
    ],
  },
  {
    jurisdiction: 'United Kingdom (HMRC)',
    fromTaxYear: '2025/26',
    toTaxYear: '2026/27',
    changes: [
      { field: 'Employee NI Main Rate (Class 1)', fromValue: '8.0%', toValue: '8.0%', impact: 'Maintained at post-Autumn cut level' },
      { field: 'Personal Allowance Freeze', fromValue: '£12,570', toValue: '£12,570', impact: 'Fiscal drag threshold maintained' },
    ],
  },
];

export const RECENT_TAX_INCIDENTS: TaxDataIncident[] = [
  {
    id: 'INC-2026-08-01',
    severity: 'INFO',
    jurisdiction: 'Canada (Ontario)',
    title: 'Ontario Health Premium Bracket Rounding Verified',
    description: 'Audit verified exact marginal phase-in thresholds for Ontario Health Premium matching CRA T4032-ON payroll tables.',
    status: 'RESOLVED',
    reportedAt: '2026-08-18 10:15 UTC',
    resolvedAt: '2026-08-18 11:30 UTC',
    resolutionNotes: 'No calculation discrepancy observed; added explicit unit fixture.',
  },
  {
    id: 'INC-2026-08-02',
    severity: 'INFO',
    jurisdiction: 'US (California FTB)',
    title: 'SDI Rate Cap Removal Confirmation',
    description: 'Confirmed California State Disability Insurance (SDI) wage ceiling removal for 2026 at 0.9% flat.',
    status: 'RESOLVED',
    reportedAt: '2026-08-15 08:45 UTC',
    resolvedAt: '2026-08-15 09:20 UTC',
    resolutionNotes: 'Updated documentation notes in US Country Pack.',
  },
];

export const DELIVERABLES_P6: DeliverableP6[] = [
  {
    code: 'DEL-P6-A',
    titleEn: 'Deterministic Progressive Tax & Deduction Pipeline',
    titleFr: 'Pipeline Déterministe d\'Imposition Progressive & Déductions',
    category: 'Precision & Mathematical Solver',
    summary: '100% deterministic, decimal arithmetic progressive bracket calculation engine with zero LLM approximation.',
    details: [
      'Multi-bracket iterative decomposition with exact marginal rate and taxable amount per tier',
      'Filing status polymorphism (Single, Married Joint, Head of Household, Married Separate)',
      'Accurate allowance phase-outs (UK £100k taper, US standard deductions, Canada BPA)',
      'Statutory social contribution wage caps and tier boundaries (OASDI, Medicare, CPP/CPP2, EI, NI, URSSAF, GKV/RV, CPF, EPF)',
    ],
    architectureNotes: 'Engine strictly separates Payroll Withholding estimates from final Annual Tax Liability declarations.',
  },
  {
    code: 'DEL-P6-B',
    titleEn: 'Reverse Net-to-Gross Numerical Solver',
    titleFr: 'Solveur Numérique Inverse Net-vers-Brut',
    category: 'Precision & Mathematical Solver',
    summary: 'Monotonic binary search solver resolving required gross salary for any target take-home pay within 1 cent tolerance.',
    details: [
      'Bounded bracket-aware binary search converging in ≤ 30 iterations',
      'Dual-pass verification: calculated net strictly matches target net',
      'Handles non-linear tax tier jumps and social security contribution ceiling transitions',
      'Fallback range estimation for complex non-monotonic subsidy edge cases',
    ],
    architectureNotes: 'Guaranteed termination with execution latency < 2ms per forward-solver cycle.',
  },
  {
    code: 'DEL-P6-C',
    titleEn: 'Employer Total Cost of Employment Engine',
    titleFr: 'Moteur de Coût Total Employeur & Charges Patronales',
    category: 'Salary & Payroll Engines',
    summary: 'Decomposes employer payroll taxes, mandatory statutory charges, and matching social security above gross wage.',
    details: [
      'Separates employee gross salary from total employer employment cost',
      'US FICA match (6.2% + 1.45%) and FUTA unemployment tax',
      'UK Employer National Insurance (13.8% above Secondary Threshold) and pension match (3%)',
      'Canada Employer CPP match (1:1) and EI premium (1.4x employee rate)',
      'France URSSAF & patronales (~42.5%), Germany Arbeitgeberanteil (~20.95%), Australia Superannuation (11.5%)',
    ],
    architectureNotes: 'Provides clear cost-ratio breakdown for compensation planning and hiring budgets.',
  },
  {
    code: 'DEL-P6-D',
    titleEn: 'Human-Readable Tax Explainability Trace',
    titleFr: 'Moteur d\'Explicabilité & Traçabilité Fiscale',
    category: 'Governance & Auditing',
    summary: 'Step-by-step plain language explanation of every tax bracket, allowance deduction, and social charge applied.',
    details: [
      'No black-box calculation: every dollar deducted is tied to an explicit rule and mathematical step',
      'Visual breakdown cards for federal, state/provincial, and local municipality taxes',
      'Distinction between marginal tax rate and effective average tax rate with intuitive tooltips',
      'Official government source attribution and verification timestamp for complete auditability',
    ],
    architectureNotes: 'Generated strictly from runtime intermediate calculation state, ensuring zero LLM hallucination.',
  },
  {
    code: 'DEL-P6-E',
    titleEn: 'Multi-Scenario & Multi-Jurisdiction Salary Comparator',
    titleFr: 'Comparateur Multi-Scénarios & Multi-Juridictions',
    category: 'UI & Command Center',
    summary: 'Side-by-side comparison of take-home pay, tax burdens, and employer costs across states, countries, and salary levels.',
    details: [
      'Compare $100k in California vs Texas, £70k vs £90k in UK, or €60k France vs Germany',
      'Local scenario saving with immutable calculation snapshots and offline localStorage caching',
      'Integrated real-time FX conversion for cross-border currency salary comparisons (via Prompt 04 fxEngine)',
      'Clear takeaway summaries highlighting take-home pay differences without political or advisory bias',
    ],
    architectureNotes: 'Allows re-running saved calculations against updated tax rulesets or viewing original historical snapshots.',
  },
  {
    code: 'DEL-P6-F',
    titleEn: 'Salary & Tax Command Center & Quality Gates',
    titleFr: 'Centre de Commandement Fiscal & Portes de Qualité',
    category: 'Governance & Auditing',
    summary: 'Real-time telemetry, 2025 vs 2026 rule diff inspector, golden test fixture suite (100% passing), and incident registry.',
    details: [
      'Global coverage matrix across 18 countries and 64 subdivisions with capability level tracking',
      'Interactive Ruleset Inspector displaying brackets, deductions, and statutory source URLs',
      'Year-over-year rule diff tracking bracket indexing and threshold expansions',
      'Continuous golden test fixture runner verifying low, median, and high-income test vectors against official schedules',
    ],
    architectureNotes: 'Fail-closed architecture: unsupported or unverified jurisdictions are explicitly labeled as partial or unavailable.',
  },
];
